import { useCallback } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import { useCompleteDemandRequestLineItemsMutation, DemandRequestLineItemStatus } from '../../../graphql'
import { useDemandRequestActions } from '../hooks/useDemandRequestActions'
import { isLineItemInTerminalState } from '../utils/demandRequestStatus'

type LineItem = {
	lineItemId: string,
	status?: DemandRequestLineItemStatus | null,
}

type DemandRequest = {
	id: string,
	lineItems?: LineItem[] | null,
}

type UseMarkRequestAsCompletedParams = {
	request: DemandRequest | undefined | null,
	addToast: ( message: string ) => void,
}

export function useMarkRequestAsCompleted( { request, addToast }: UseMarkRequestAsCompletedParams ) {
	const { markAsCompleted } = useDemandRequestActions()
	const [ completeLineItems ] = useCompleteDemandRequestLineItemsMutation()

	const handleMarkRequestAsCompleted = useCallback( async() => {
		if ( !request ) {
			return
		}

		const openLineItemIds = ( request.lineItems ?? [] )
			.filter( ( item ) => !isLineItemInTerminalState( item.status ) )
			.map( ( item ) => item.lineItemId )

		if ( openLineItemIds.length > 0 ) {
			try {
				const result = await completeLineItems( {
					variables: {
						input: {
							demandRequestId: request.id,
							lineItemIds: openLineItemIds,
						},
					},
				} )

				if ( !result.data?.completeDemandRequestLineItems?.success ) {
					const message = result.data?.completeDemandRequestLineItems?.message ?? 'Unknown error occurred'
					addToast( `Failed to complete line items: ${message}. Request was not marked as completed.` )
					return
				}
			} catch ( err ) {
				addToast( `Failed to complete line items: ${err instanceof Error ? err.message : 'Unknown error'}. Request was not marked as completed.` )
				return
			}
		}

		await markAsCompleted( request.id )

		heapEvent( 'Inventory - Stock Request - Header - Click - Mark As Completed', {
			demand_request_id: request.id,
			open_line_items_completed: openLineItemIds.length,
		} )
	}, [ request, completeLineItems, markAsCompleted, addToast ] )

	return { handleMarkRequestAsCompleted }
}
